'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface BorderCrossing {
  id: string;
  name: string;
  from: string;
  to: string;
  avgTime: string;
  peakTime: string;
  documents: string[];
}

interface BorderCrossingEstimatorProps {
  crossings: BorderCrossing[];
}

const BorderCrossingEstimator = ({
  crossings,
}: BorderCrossingEstimatorProps) => {
  const [selectedCrossing, setSelectedCrossing] = useState<string>(
    crossings[0]?.id || ''
  );

  const currentCrossing = crossings.find((c) => c.id === selectedCrossing);

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6">
      <div className="mb-6">
        <h3 className="font-headline font-bold text-xl text-foreground mb-2">
          Border Crossing Time Estimator
        </h3>
        <p className="font-body text-sm text-muted-foreground">
          Real-time estimates for major border crossings
        </p>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-body font-semibold text-foreground mb-2">
          Select Border Crossing
        </label>
        <select
          value={selectedCrossing}
          onChange={(e) => setSelectedCrossing(e.target.value)}
          className="w-full px-4 py-3 bg-background border border-border rounded-lg font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-golden-mid transition-all duration-300"
        >
          {crossings.map((crossing) => (
            <option key={crossing.id} value={crossing.id}>
              {crossing.name} ({crossing.from} → {crossing.to})
            </option>
          ))}
        </select>
      </div>

      {currentCrossing && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
              <div className="flex items-center space-x-3 mb-2">
                <Icon name="ClockIcon" size={20} className="text-success" />
                <span className="text-xs font-body text-muted-foreground">
                  Average Processing Time
                </span>
              </div>
              <p className="text-2xl font-headline font-bold text-success">
                {currentCrossing.avgTime}
              </p>
            </div>

            <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex items-center space-x-3 mb-2">
                <Icon name="ExclamationTriangleIcon" size={20} className="text-warning" />
                <span className="text-xs font-body text-muted-foreground">
                  Peak Hours Estimate
                </span>
              </div>
              <p className="text-2xl font-headline font-bold text-warning">
                {currentCrossing.peakTime}
              </p>
            </div>
          </div>

          <div>
            <h4 className="font-headline font-bold text-base text-foreground mb-3 flex items-center space-x-2">
              <Icon name="DocumentCheckIcon" size={18} className="text-golden-mid" />
              <span>Required Documentation</span>
            </h4>
            <ul className="space-y-2">
              {currentCrossing.documents.map((doc, idx) => (
                <li
                  key={idx}
                  className="flex items-start space-x-2 font-body text-sm text-muted-foreground"
                >
                  <Icon
                    name="CheckCircleIcon"
                    size={16}
                    className="text-success mt-0.5 flex-shrink-0"
                  />
                  <span>{doc}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-4 bg-muted rounded-lg border border-border">
            <div className="flex items-start space-x-3">
              <Icon
                name="InformationCircleIcon"
                size={20}
                className="text-primary mt-0.5 flex-shrink-0"
              />
              <div>
                <p className="font-body text-sm text-foreground leading-relaxed">
                  Times are estimates based on recent crossings. Actual processing times may vary based on cargo type, documentation completeness, and current border traffic. Our team provides real-time updates during transit.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BorderCrossingEstimator;